import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';
import { formatDate, formatMoney, todayISO } from '../lib/format';
import type { AccountType } from '../lib/types';

interface Row { code: string; name: string; type: AccountType; amount: number; }

export default function ReportBalanceSheet() {
  const { currentOrg } = useOrg();
  const [asOf, setAsOf] = useState(todayISO());
  const [rows, setRows] = useState<Row[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (currentOrg) run(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [asOf, currentOrg]);

  async function run() {
    if (!currentOrg) return;
    setBusy(true);
    // Balance sheet = cumulative debits-credits up to as-of date, on
    // asset/liability/equity/contra accounts. Retained earnings is the
    // closing P&L net through the same date.
    const { data } = await supabase
      .from('dodobooks_journal_lines')
      .select(`
        debit, credit,
        account:dodobooks_accounts!inner(code, name, account_type, org_id),
        journal:dodobooks_journals!inner(date, org_id, is_posted)
      `)
      .eq('account.org_id', currentOrg.id)
      .eq('journal.org_id', currentOrg.id)
      .eq('journal.is_posted', true)
      .lte('journal.date', asOf);

    const agg: Record<string, Row> = {};
    let plNet = 0;
    for (const r of (data ?? []) as any[]) {
      const a = r.account;
      if (!a) continue;
      const t = a.account_type as AccountType;
      const d = Number(r.debit), c = Number(r.credit);
      if (t === 'asset') {
        const key = a.code;
        if (!agg[key]) agg[key] = { code: a.code, name: a.name, type: t, amount: 0 };
        agg[key].amount += d - c;
      } else if (t === 'liability' || t === 'equity') {
        const key = a.code;
        if (!agg[key]) agg[key] = { code: a.code, name: a.name, type: t, amount: 0 };
        agg[key].amount += c - d;
      } else if (t === 'contra') {
        const key = a.code;
        if (!agg[key]) agg[key] = { code: a.code, name: a.name, type: t, amount: 0 };
        // Contra-asset accumulates as credits (reduces asset)
        agg[key].amount += c - d;
      } else if (t === 'revenue') {
        plNet += c - d;
      } else if (t === 'expense' || t === 'cogs') {
        plNet += -(d - c);
      }
    }
    const out = Object.values(agg).sort((a, b) => a.code.localeCompare(b.code));
    // Synthetic line for retained earnings (P&L flowing into equity).
    out.push({ code: '3500-calc', name: 'Retained earnings (current period P&L)', type: 'equity', amount: plNet });
    setRows(out);
    setBusy(false);
  }

  const assets = rows.filter((r) => r.type === 'asset');
  const contras = rows.filter((r) => r.type === 'contra');
  const liabilities = rows.filter((r) => r.type === 'liability');
  const equity = rows.filter((r) => r.type === 'equity');
  const assetsTotal = assets.reduce((s, r) => s + r.amount, 0) - contras.reduce((s, r) => s + r.amount, 0);
  const liabTotal = liabilities.reduce((s, r) => s + r.amount, 0);
  const equityTotal = equity.reduce((s, r) => s + r.amount, 0);
  const totalLiabEquity = liabTotal + equityTotal;
  const balanced = Math.abs(assetsTotal - totalLiabEquity) < 0.01;

  return (
    <div className="flex flex-col gap-6">
      <header>
        <h1 className="h-page">Balance sheet</h1>
        <p className="text-text-dim text-sm mt-1">Assets, liabilities and equity as at a given date.</p>
      </header>

      <div className="card grid grid-cols-2 gap-4 max-w-md">
        <div className="field"><label>As at</label><input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} /></div>
        <div className="flex items-end">
          <span className={'pill ' + (balanced ? 'pill-paid' : 'pill-overdue')}>{balanced ? 'Balances' : 'Out of balance!'}</span>
        </div>
      </div>

      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="h-section">{currentOrg?.name}</h2>
          <span className="text-xs text-text-mute font-mono">As at {formatDate(asOf)}</span>
        </div>

        {busy ? (
          <div className="text-text-mute text-sm py-8 text-center">Calculating…</div>
        ) : (
          <div className="grid grid-cols-2 gap-8">
            <div>
              <h3 className="font-serif text-base font-semibold mb-2">Assets</h3>
              <table>
                <tbody>
                  {assets.map((r) => (
                    <tr key={r.code}><td className="text-text-dim">{r.code} · {r.name}</td><td className="text-right num">{formatMoney(r.amount)}</td></tr>
                  ))}
                  {contras.map((r) => (
                    <tr key={r.code}><td className="text-text-dim italic">{r.code} · {r.name}</td><td className="text-right num text-rust">({formatMoney(r.amount)})</td></tr>
                  ))}
                  <tr><td className="font-serif font-semibold pt-2 border-t border-line">Total assets</td><td className="text-right num font-semibold pt-2 border-t border-line">{formatMoney(assetsTotal)}</td></tr>
                </tbody>
              </table>
            </div>
            <div>
              <h3 className="font-serif text-base font-semibold mb-2">Liabilities</h3>
              <table>
                <tbody>
                  {liabilities.map((r) => (
                    <tr key={r.code}><td className="text-text-dim">{r.code} · {r.name}</td><td className="text-right num">{formatMoney(r.amount)}</td></tr>
                  ))}
                  <tr><td className="font-serif font-semibold pt-2 border-t border-line">Total liabilities</td><td className="text-right num font-semibold pt-2 border-t border-line">{formatMoney(liabTotal)}</td></tr>
                </tbody>
              </table>
              <h3 className="font-serif text-base font-semibold mb-2 mt-6">Equity</h3>
              <table>
                <tbody>
                  {equity.map((r) => (
                    <tr key={r.code}><td className="text-text-dim">{r.code === '3500-calc' ? '' : `${r.code} · `}{r.name}</td><td className="text-right num">{formatMoney(r.amount)}</td></tr>
                  ))}
                  <tr><td className="font-serif font-semibold pt-2 border-t border-line">Total equity</td><td className="text-right num font-semibold pt-2 border-t border-line">{formatMoney(equityTotal)}</td></tr>
                </tbody>
              </table>
              <div className="mt-4 pt-3 border-t-2 border-emerald/30 flex justify-between font-serif text-base font-semibold">
                <span>Total liab. + equity</span>
                <span className={'num ' + (balanced ? 'text-emerald' : 'text-rust')}>{formatMoney(totalLiabEquity)}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
